import React from 'react';
import { Mail, Phone, Globe, Linkedin, Twitter, Facebook } from 'lucide-react';
import { companyData } from '../mock';
import PrivacyPolicy from './PrivacyPolicy';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'About Us', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Projects', href: '#projects' },
    { name: 'Clients', href: '#clients' }
  ];

  const services = [
    { name: 'Erection & Commissioning', href: '#services' },
    { name: 'Renovation & Modernization', href: '#services' },
    { name: 'Equipment & Machinery', href: '#equipment' },
    { name: 'Certifications', href: '#certifications' }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-900 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div>
            <div className="mb-6 flex items-center space-x-3">
              <img 
                src="/unified-power-logo.png" 
                alt="Unified Power Logo" 
                className="h-16 w-auto object-contain"
              />
              <div>
                <h3 className="text-2xl font-bold mb-1">UNIFIED POWER</h3>
                <p className="text-xs text-slate-400 tracking-wider">PRIVATE LIMITED</p>
              </div>
            </div>
            <p className="text-slate-400 mb-4 text-sm leading-relaxed">
              Excellence in power plant engineering since {companyData.established}. Specializing in erection, renovation, and maintenance of thermal and nuclear power plants.
            </p>
            <div className="flex space-x-3">
              <a href="#" className="w-10 h-10 bg-slate-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors duration-200">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors duration-200">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-slate-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors duration-200">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Services */}
          <div>
            <h4 className="text-lg font-bold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <a
                    href={service.href}
                    onClick={(e) => scrollToSection(e, service.href)}
                    className="text-slate-400 hover:text-blue-400 transition-colors duration-200 text-sm"
                  >
                    {service.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  {companyData.phones.map((phone, index) => (
                    <a key={index} href={`tel:${phone}`} className="text-slate-400 hover:text-blue-400 transition-colors text-sm block">
                      {phone}
                    </a>
                  ))}
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <a href={`mailto:${companyData.email}`} className="text-slate-400 hover:text-blue-400 transition-colors text-sm">
                  {companyData.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Globe className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <a href={`https://${companyData.website}`} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-400 transition-colors text-sm">
                  {companyData.website}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-slate-400 text-sm">
              © {currentYear} Unified Power Private Limited. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm">
              <a 
                href="/UPPL_Professional_Profile.pdf" 
                download
                className="text-slate-400 hover:text-blue-400 transition-colors flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Company Profile
              </a>
              <PrivacyPolicy>
                <button className="text-slate-400 hover:text-blue-400 transition-colors">
                  Privacy Policy
                </button>
              </PrivacyPolicy>
              <button className="text-slate-400 hover:text-blue-400 transition-colors">
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
